// 149-cw-r86 (2026-08-27) — WO Stage Info Dialog.
// Muncul saat user klik tombol (i) di chevron stage. BOD Lv1 bisa Edit isi
// keterangan (persist ke /api/wo-stage-notes/{key}). Non-admin: view-only.
// 149-cw-r109 (2026-08-28) — Setiap chevron punya DEFAULT description
// (definisi + syarat + logika transisi) yang tampil bila belum ada custom note
// dari BOD. Membantu user paham kapan angka bertambah/berpindah antar chevron.
// 149-cw-r132 (2026-08-31) — Format DEFAULT description dibakukan menjadi
// 3 section eksplisit: DEFINISI, BERTAMBAH, BERKURANG — supaya user jelas
// tahu kapan counter chevron naik/turun tanpa harus baca kode.
// 2026-06-20 (DUAL WORKFLOW) — Deskripsi ditulis ulang mengikuti ENGINE
// CANONICAL `utils/wo_workflow.py` / `lib/woWorkflow.js`: chevron MUTUALLY
// EXCLUSIVE (satu WO tepat satu chevron), dua alur (Non-Urgent & Urgent),
// dan chevron baru "Kategori Belum Ditentukan".
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, X } from "lucide-react";

// 2026-06-20: Setiap chevron dijelaskan dengan pembedaan alur WO NON-URGENT
// (alur standar: Survey → SPH & Negosiasi → PO → Pengerjaan → Selesai Fisik →
// Invoice) dan WO URGENT (alur cepat: Pengerjaan boleh SEBELUM SPH/PO —
// Survey → Pengerjaan → Selesai Fisik → SPH & Negosiasi → PO → Invoice).
// Flag WO Urgent = `is_urgent = true` di project doc.
const STAGE_DEFAULT_DESCRIPTIONS = {
  wo_diterima: {
    body: `DEFINISI:
WO yang belum memiliki milestone apapun — belum site survey, belum SPH, belum PO, dan belum mulai pengerjaan. Berlaku sama untuk WO Non-Urgent maupun Urgent. SATU WO hanya dihitung di SATU chevron (eksklusif — chevron ini BUKAN lagi total kumulatif).

BERTAMBAH (+1) saat:
• BOTH: WO baru dibuat (dengan/tanpa flag Urgent) dan belum ada tanggal milestone yang diisi.

BERKURANG (−1) saat:
• NON-URGENT: \`site_survey_date\` diisi → pindah ke "Site Survey".
• URGENT: \`site_survey_date\` diisi → "Site Survey"; \`work_start_date\` langsung diisi → "Pengerjaan".
• BOTH: WO di-Close / di-Cancel-by-Owner → keluar dari chevron aktif.`,
  },
  site_survey: {
    body: `DEFINISI:
WO yang \`site_survey_date\` sudah terisi. NON-URGENT: SPH belum pernah dikirim. URGENT: pengerjaan fisik belum dimulai (alur urgent: survey → langsung pengerjaan, SPH/PO menyusul).

BERTAMBAH (+1) saat:
• BOTH: \`site_survey_date\` diisi via Edit WO (pindah dari "WO Diterima").

BERKURANG (−1) saat:
• NON-URGENT: SPH pertama dikirim → pindah ke "SPH & Negosiasi"; PO terbit → pindah ke "Pengerjaan".
• URGENT: \`work_start_date\` diisi → pindah ke "Pengerjaan"; \`project_done_date\` diisi → "Selesai Fisik".
• BOTH: WO di-Close/Cancel di tahap ini.`,
  },
  sph_negosiasi: {
    body: `DEFINISI:
SPH sudah dikirim namun PO belum terbit. NON-URGENT: tahap negosiasi setelah survey, sebelum pengerjaan. URGENT: pekerjaan SUDAH selesai fisik, SPH menyusul dikirim sambil menunggu PO (status tampil: "Selesai Fisik · Menunggu PO").

BERTAMBAH (+1) saat:
• NON-URGENT: SPH pertama dikirim (pindah dari "Site Survey").
• URGENT: \`sph_submit_date\` diisi setelah pekerjaan selesai fisik (pindah dari "Selesai Fisik").

BERKURANG (−1) saat:
• NON-URGENT: \`po_number\` diisi → pindah ke "Pengerjaan".
• URGENT: \`po_number\` diisi → langsung ke "Pembuatan Invoice" (pengerjaan & selesai fisik sudah lampau — ini BENAR untuk alur urgent, bukan kesalahan urutan).
• BOTH: WO di-Close/Cancel di tahap ini.`,
  },
  pengerjaan: {
    body: `DEFINISI:
Pekerjaan fisik sedang berjalan (belum ada tanggal selesai fisik). NON-URGENT: masuk tahap ini setelah PO terbit (PO wajib mendahului pengerjaan). URGENT: masuk tahap ini begitu \`work_start_date\` diisi — SAH meski SPH/PO belum ada (alur cepat).

BERTAMBAH (+1) saat:
• NON-URGENT: \`po_number\` diisi (pindah dari "SPH & Negosiasi").
• URGENT: \`work_start_date\` diisi (pindah dari "Site Survey" / "WO Diterima").

BERKURANG (−1) saat:
• NON-URGENT: \`project_done_date\` diisi → pindah ke "Pembuatan Invoice".
• URGENT: \`project_done_date\` diisi → pindah ke "Selesai Fisik" (bila SPH belum dibuat).
• BOTH: WO di-Cancel di tengah pengerjaan.

CATATAN: WO NON-URGENT dengan tanggal pengerjaan/selesai SEBELUM PO ditandai "Perlu Verifikasi" — urutan milestone tidak sesuai alur normal dan perlu dicek manual.`,
  },
  selesai_fisik: {
    body: `DEFINISI:
CIRI KHAS WO URGENT — pekerjaan selesai fisik TAPI SPH belum dibuat sama sekali. Status tampil: "Selesai Fisik · SPH Belum Dibuat". Tindakan berikutnya: Buat & kirim SPH.

BERTAMBAH (+1) saat:
• URGENT: \`project_done_date\` diisi sementara SPH belum ada (pindah dari "Pengerjaan").

BERKURANG (−1) saat:
• URGENT: \`sph_submit_date\` diisi → pindah ke "SPH & Negosiasi".
• BOTH: WO di-Close/Cancel di tahap ini.

CATATAN: Keterlambatan di tahap ini adalah keterlambatan ADMINISTRASI ("Terlambat SPH"), bukan keterlambatan pekerjaan — pekerjaan fisik sudah tuntas.`,
  },
  pembuatan_invoice: {
    body: `DEFINISI:
Pekerjaan selesai fisik dan PO sudah terbit, TAPI invoice belum dibuat sama sekali (belum ada income entry dengan \`invoice_date\` non-retensi). Berlaku sama untuk kedua alur.

BERTAMBAH (+1) saat:
• NON-URGENT: \`project_done_date\` diisi setelah PO ada (pindah dari "Pengerjaan").
• URGENT: \`po_number\` menyusul diisi setelah selesai fisik (pindah dari "SPH & Negosiasi").

BERKURANG (−1) saat:
• BOTH: Invoice pertama dibuat di Laporan Keuangan (\`invoice_date\` terisi) → pindah ke "Pengiriman Invoice".
• BOTH: WO di-Close/Cancel di tahap ini.`,
  },
  pengiriman_invoice: {
    body: `DEFINISI:
Invoice sudah dibuat (ada \`invoice_date\` pada income entry non-retensi) namun belum ditandai terkirim ke client (\`invoice_sent_date\` kosong).

BERTAMBAH (+1) saat:
• BOTH: Invoice pertama dibuat (pindah dari "Pembuatan Invoice").

BERKURANG (−1) saat:
• BOTH: \`invoice_sent_date\` diisi → pindah ke "Invoice / Menunggu Penyelesaian".
• BOTH: WO di-Close/Cancel di tahap ini.`,
  },
  invoice_pending: {
    body: `DEFINISI:
Invoice sudah terkirim ke client — WO menunggu pembayaran & penyelesaian administrasi sebelum di-Close. Bila melewati tenggat, label keterlambatannya SPESIFIK: "Pembayaran Terlambat".

BERTAMBAH (+1) saat:
• BOTH: \`invoice_sent_date\` diisi (pindah dari "Pengiriman Invoice").

BERKURANG (−1) saat:
• BOTH: WO di-Close (\`status = completed\`, biasanya setelah pembayaran lunas) → pindah ke "Closed".
• BOTH: WO di-Cancel-by-Owner → pindah ke "Dibatalkan Owner".`,
  },
  kategori_belum_ditentukan: {
    body: `DEFINISI:
WO yang field kategorinya (\`is_urgent\`) belum diisi — sistem TIDAK menebak alur kerjanya. Chevron ini berdiri sendiri: TIDAK dihitung sebagai "Pekerjaan Aktif" maupun "Selesai Fisik — Administrasi Terbuka".

BERTAMBAH (+1) saat:
• WO lama/baru yang kategorinya belum pernah ditentukan.

BERKURANG (−1) saat:
• Kategori ditentukan (Urgent / Non-Urgent) via Edit WO → WO otomatis masuk alur & chevron yang sesuai.
• WO di-Close/Cancel.

CATATAN: Tindakan berikutnya untuk WO di sini: tentukan kategori WO (Urgent / Non-Urgent) agar pelacakan chevron akurat.`,
  },
  closed: {
    body: `DEFINISI:
WO yang siklus operasional-nya sudah tuntas (\`status = completed\`). Terminal state — TIDAK masuk chevron aktif. Berlaku sama untuk kedua alur.

BERTAMBAH (+1) saat:
• BOTH: BOD/PIC klik "Close WO" → \`status = completed\` (setelah pembayaran full + retensi cair).

BERKURANG (−1) saat:
• BOTH: WO di-Reopen manual oleh BOD (langka — untuk koreksi salah close). Terminal state; tidak berpindah otomatis ke chevron lain.`,
  },
  cancelled_owner: {
    body: `DEFINISI:
WO yang dibatalkan oleh client / owner sebelum selesai (\`status = cancelled_by_owner\`). Terminal state — TIDAK masuk chevron aktif dan TIDAK dihitung "Pekerjaan Aktif". Di ringkasan kategori, WO dibatalkan dilaporkan terpisah dari Urgent/Non-Urgent.

BERTAMBAH (+1) saat:
• BOTH: BOD/PIC klik tombol "Cancel by Owner" + isi alasan → \`status = cancelled_by_owner\`.

BERKURANG (−1) saat:
• BOTH: WO di-Reopen manual oleh BOD (langka — untuk koreksi). Terminal state; tidak berpindah otomatis.`,
  },
};

export default function WOStageInfoDialog({ open, onOpenChange, stage, user }) {
  // stage = { key, label }
  const [note, setNote] = useState(null); // { content, updated_at, updated_by_name } | null
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.division === "BOD" && Number(user?.level || 5) === 1;

  useEffect(() => {
    if (!open || !stage?.key) return;
    setEditing(false);
    setLoading(true);
    api.get("/wo-stage-notes")
      .then(({ data }) => {
        const n = data?.[stage.key];
        setNote(n || null);
        setDraft(n?.content || "");
      })
      .catch(() => setNote(null))
      .finally(() => setLoading(false));
  }, [open, stage?.key]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/wo-stage-notes/${stage.key}`, { content: draft });
      setNote(data);
      setEditing(false);
      toast.success("Keterangan tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  if (!stage) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-none" data-testid="wo-stage-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center border border-slate-400 text-slate-700 font-bold">i</span>
            {stage.label}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Keterangan tahapan ini. {isSuperAdmin ? "Anda (BOD Lv1) bisa mengubah isinya." : "Hanya Super Admin (BOD Lv1) yang bisa mengubah isinya."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-[120px] py-2" data-testid="wo-stage-info-body">
          {loading ? (
            <div className="text-xs text-slate-400 italic">Memuat...</div>
          ) : editing ? (
            <textarea
              data-testid="wo-stage-info-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={6}
              maxLength={2000}
              placeholder="Tulis keterangan tahapan ini..."
              className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
            />
          ) : (note?.content ? (
            <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="wo-stage-info-custom">{note.content}</div>
          ) : (
            (() => {
              const def = STAGE_DEFAULT_DESCRIPTIONS[stage.key];
              if (!def) return <div className="text-xs text-slate-400 italic">Belum ada keterangan untuk tahapan ini.</div>;
              // r132: render dgn highlight untuk sub-heading DEFINISI/BERTAMBAH/BERKURANG.
              const lines = String(def.body || "").split("\n");
              return (
                <div data-testid="wo-stage-info-default" className="space-y-0.5 text-sm text-slate-800 leading-relaxed">
                  {lines.map((ln, i) => {
                    const trimmed = ln.trim();
                    if (/^DEFINISI:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-[#0B1D3A] pt-2 first:pt-0">Definisi</div>;
                    if (/^BERTAMBAH.*:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 pt-3">Kapan Bertambah ↑</div>;
                    if (/^BERKURANG.*:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-rose-700 pt-3">Kapan Berkurang ↓</div>;
                    if (/^CATATAN.*:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-amber-700 pt-3">{trimmed.replace(/:$/,"")}</div>;
                    if (trimmed === "") return <div key={i} className="h-1" />;
                    return <div key={i} className="whitespace-pre-wrap">{ln}</div>;
                  })}
                  <div className="text-[10px] text-slate-400 italic mt-4 border-t border-slate-100 pt-2">
                    Ini keterangan bawaan sistem. {isSuperAdmin ? "Klik Edit untuk override dengan versi custom." : "Hanya Super Admin (BOD Lv1) yang bisa mengubah."}
                  </div>
                </div>
              );
            })()
          ))}
          {note?.updated_at && !editing && (
            <div className="text-[10px] text-slate-400 italic mt-3 border-t border-slate-100 pt-2">
              Diubah oleh {note.updated_by_name || "-"} · {new Date(note.updated_at).toLocaleString("id-ID")}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {isSuperAdmin && !editing && (
            <Button onClick={() => { setDraft(note?.content || ""); setEditing(true); }} variant="outline" className="rounded-none" data-testid="wo-stage-info-edit-btn">
              <Pencil size={12} className="mr-1"/> Edit
            </Button>
          )}
          {editing && (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-none" data-testid="wo-stage-info-cancel-btn">
                <X size={12} className="mr-1"/> Batal
              </Button>
              <Button onClick={save} disabled={saving} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white" data-testid="wo-stage-info-save-btn">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </>
          )}
          {!editing && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Tutup</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
